/**
 * PromoService — Promotional pricing calculations & helpers.
 *
 * Centralises all promo-price logic so that Menu UI, Digital Preview,
 * and God Mode all share a single source of truth.
 */

import type { MenuItem } from '../types';

// ─── Price Calculation ───────────────────────────────────────────────────────

/**
 * Calculate the promo price given an original price and campaign discount.
 * Returns a rounded-to-2-decimal number.
 */
export function calcPromoPrice(
  originalPrice: number,
  discountValue: number,
  discountType: string,
): number {
  if (discountType === 'FLAT') {
    return Math.max(0, +(originalPrice - discountValue).toFixed(2));
  }
  // PERCENTAGE (default)
  return Math.max(0, +(originalPrice * (1 - discountValue / 100)).toFixed(2));
}

// ─── Display Helpers ─────────────────────────────────────────────────────────

/**
 * Returns the effective display price for a menu item.
 * If the item has campaignMeta and is not expired, returns promoPrice.
 * Otherwise returns basePrice.
 */
export function getDisplayPrice(item: MenuItem): number {
  if (item.campaignMeta && !isPromoExpired(item)) {
    return item.campaignMeta.promoPrice;
  }
  return item.basePrice;
}

/**
 * Check if a promo-item's campaign period has expired.
 */
export function isPromoExpired(item: MenuItem): boolean {
  if (!item.campaignMeta) return false;
  return Date.now() > item.campaignMeta.endDate;
}

/**
 * Check if the promo period has started.
 */
export function isPromoActive(item: MenuItem): boolean {
  if (!item.campaignMeta) return false;
  const now = Date.now();
  return now >= item.campaignMeta.startDate && now <= item.campaignMeta.endDate;
}

/**
 * Human-readable discount label, e.g. "15% OFF" or "QAR 5 OFF".
 */
export function discountLabel(item: MenuItem, currency?: string): string {
  if (!item.campaignMeta) return '';
  const { discountValue, discountType } = item.campaignMeta;
  if (discountType === 'FLAT') {
    return `${currency ?? ''} ${discountValue} OFF`.trim();
  }
  return `${discountValue}% OFF`;
}

/**
 * Funding label for badge display, e.g. "Partner Funded", "Co-Funded".
 */
export function fundingLabel(item: MenuItem): string {
  if (!item.campaignMeta) return '';
  switch (item.campaignMeta.fundingType) {
    case 'FULLY_FUNDED': return 'Partner Funded';
    case 'CO_FUNDED': return 'Co-Funded';
    default: return 'Self Funded';
  }
}

// ─── Financial Breakdown (God Mode) ─────────────────────────────────────────

export interface FinancialBreakdown {
  originalPrice: number;
  promoPrice: number;
  discountAmount: number;
  sterlingShare: number;
  partnerShare: number;
  fundingType: string;
}

/**
 * Compute a full financial breakdown for God Mode inspection.
 */
export function getFinancialBreakdown(item: MenuItem): FinancialBreakdown | null {
  if (!item.campaignMeta) return null;
  const { originalPrice, promoPrice, fundingType, sterlingSharePct, partnerSharePct } = item.campaignMeta;
  const discountAmount = +(originalPrice - promoPrice).toFixed(2);

  let sterlingShare = 0;
  let partnerShare = 0;
  if (fundingType === 'FULLY_FUNDED') {
    partnerShare = discountAmount;
  } else if (fundingType === 'CO_FUNDED') {
    sterlingShare = +(discountAmount * ((sterlingSharePct ?? 50) / 100)).toFixed(2);
    partnerShare = +(discountAmount - sterlingShare).toFixed(2);
  } else {
    sterlingShare = discountAmount;
  }

  return { originalPrice, promoPrice, discountAmount, sterlingShare, partnerShare, fundingType };
}
